import { useEffect } from 'react';
import PropTypes from 'prop-types';

import { useHTTP } from 'hooks/useRequest';
import isMobileApp from 'utils/isMobileApp';
import { branchTermSingularCapitalised } from 'utils/terminology';
import useProductShowStore from 'stores/product_show_store/useProductShowStore';

import AddToBasket from 'components/product_show/add_to_basket/add_to_basket';
import ReserveAndCollect from 'components/product_show/reserve_and_collect/reserve_and_collect';
import CEFQuantitySelector, {
  QuantitySelectorError,
} from 'components/product_show/add_to_basket/quantity_selector';
import BackOrderModal from 'components/product_show/add_to_basket/back_order_modal';
import SecondaryProductActions from 'components/product_show/secondary_product_actions';

const ProductActions = ({
  title,
  brand,
  isSimilarStock,
  category,
  partCode,
  isDeliveryOnly,
  isStoreSelected,
  storeStockUrl,
  addToBasketUrl,
  similarStockUrl,
  vatExclusive,
  disabled,
  minOrderValue,
  maxOrderValue,
  fetchPriceUrl,
  deliveryStock,
  deliveryStockEta,
  specialOrderMessage,
  isCollectionOnly,
  stepId,
  stockCode,
}) => {
  const {
    quantitySelectorError,
    showBackOrderModal,
    closeBackOrderModal,
    productStock,
    productBasketQuantity,
    backOrderSuccessCallback,
    setSelectedStore,
    setStoreStock,
    setLocationId,
  } = useProductShowStore();

  const showSecondaryProductActions = !isCollectionOnly || isStoreSelected;

  const { data: storeStockResponse } = useHTTP({
    method: 'GET',
    url: isStoreSelected && !isDeliveryOnly ? storeStockUrl : null,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  useEffect(() => {
    if (storeStockResponse && storeStockResponse[0]) {
      const {
        products,
        store_name: store,
        location_id: sLocationId,
      } = storeStockResponse[0];
      const storeName = store.replace('Branch', branchTermSingularCapitalised);
      const storeStockAmount = products.length
        ? parseInt(products[0].stock)
        : null;

      setLocationId(sLocationId);
      setStoreStock(storeStockAmount);
      setSelectedStore(storeName);
    }
  }, [storeStockResponse]);

  const handleModalSuccess = () => {
    if (backOrderSuccessCallback) {
      backOrderSuccessCallback();
    }
    closeBackOrderModal();
  };

  return (
    <div className="product-actions">
      <div className="fl fl-col g-16">
        <div
          className="fl fl-row g-12 ai-c"
          data-testid="product-actions__quantity-selector">
          <CEFQuantitySelector
            stepId={stepId}
            minOrderValue={minOrderValue}
            maxOrderValue={maxOrderValue}
            fetchPriceUrl={fetchPriceUrl}
          />
          {quantitySelectorError && (
            <QuantitySelectorError
              quantitySelectorError={quantitySelectorError}
            />
          )}
        </div>
        {!isMobileApp() && (
          <div className="product-actions__buttons fl fl-row">
            <div className="fl-grow-1 fb-0" data-testid="product-actions__ctas">
              <AddToBasket
                stepId={stepId}
                addToBasketUrl={addToBasketUrl}
                stockCode={stockCode}
                brand={brand}
                title={title}
                category={category}
                partCode={partCode}
                isReserveCollect={false}
                isCollectionOnly={isCollectionOnly}
                deliveryStock={deliveryStock}
                deliveryStockEta={deliveryStockEta}
                specialOrderMessage={specialOrderMessage}
                isSimilarStock={isSimilarStock}
                showStock
              />
            </div>
            <div className="fl-grow-1 fb-0">
              <ReserveAndCollect
                isStoreSelected={isStoreSelected}
                isDeliveryOnly={isDeliveryOnly}
                stepId={stepId}
                storeStockUrl={storeStockUrl}
                addToBasketUrl={addToBasketUrl}
                title={title}
                category={category}
                stockCode={stockCode}
                partCode={partCode}
                brand={brand}
                vatExclusive={vatExclusive}
                similarStockUrl={similarStockUrl}
                isSimilarStock={isSimilarStock}
                disabled={disabled}
                showStock
              />
            </div>
          </div>
        )}
        {showSecondaryProductActions && (
          <SecondaryProductActions
            similarStockUrl={similarStockUrl}
            deliveryStock={deliveryStock}
            vatExclusive={vatExclusive}
            stepId={stepId}
            isCollectionOnly={isCollectionOnly}
          />
        )}
      </div>

      <BackOrderModal
        modalIsOpen={showBackOrderModal}
        closeModal={() => closeBackOrderModal()}
        stockLevel={productStock}
        totalBasketQuantity={productBasketQuantity}
        successCallback={handleModalSuccess}
      />
    </div>
  );
};

ProductActions.propTypes = {
  title: PropTypes.string,
  brand: PropTypes.string,
  isSimilarStock: PropTypes.bool,
  category: PropTypes.string,
  partCode: PropTypes.string,
  isDeliveryOnly: PropTypes.bool,
  isStoreSelected: PropTypes.bool,
  storeStockUrl: PropTypes.string,
  addToBasketUrl: PropTypes.string,
  similarStockUrl: PropTypes.string,
  vatExclusive: PropTypes.bool,
  disabled: PropTypes.bool,
  minOrderValue: PropTypes.number,
  maxOrderValue: PropTypes.number,
  fetchPriceUrl: PropTypes.string,
  deliveryStock: PropTypes.number,
  deliveryStockEta: PropTypes.string,
  specialOrderMessage: PropTypes.string,
  isCollectionOnly: PropTypes.bool,
  stepId: PropTypes.string,
  stockCode: PropTypes.string,
};

export default ProductActions;
